import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import { AccessRecord, AdminUser } from '../../types';
import { accessService } from '../../services/accessService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

interface AccessManagerViewProps {
    currentUser: AdminUser | null;
}

const AccessManagerView: React.FC<AccessManagerViewProps> = ({ currentUser }) => {
    const [records, setRecords] = useState<AccessRecord[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<AccessRecord | null>(null);
    const [showPasswords, setShowPasswords] = useState<{ [key: string]: boolean }>({});
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

    // Form state
    const [formData, setFormData] = useState({
        websiteName: '',
        url: '',
        username: '',
        password: '',
        notes: ''
    });

    useEffect(() => {
        const unsubscribe = accessService.getAll(
            (data) => {
                setRecords(data);
                setIsLoading(false);
            },
            (error) => {
                console.error("Error fetching access records:", error);
                setIsLoading(false);
            }
        );
        return () => unsubscribe();
    }, []);

    const filteredRecords = useMemo(() => {
        if (!searchQuery.trim()) return records;
        const query = searchQuery.toLowerCase();
        return records.filter(r => 
            r.websiteName.toLowerCase().includes(query) ||
            r.username.toLowerCase().includes(query) ||
            r.url.toLowerCase().includes(query) ||
            (r.notes || '').toLowerCase().includes(query)
        );
    }, [records, searchQuery]);

    const handleOpenModal = (record: AccessRecord | null = null) => {
        if (record) {
            setEditingRecord(record);
            setFormData({
                websiteName: record.websiteName,
                url: record.url,
                username: record.username,
                password: record.password,
                notes: record.notes || ''
            });
        } else {
            setEditingRecord(null);
            setFormData({
                websiteName: '',
                url: '',
                username: '',
                password: '',
                notes: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingRecord) {
                await accessService.update(editingRecord.id, formData);
                toast.success('Credential updated successfully');
            } else {
                await accessService.create(formData);
                toast.success('Credential added successfully');
            }
            setIsModalOpen(false);
        } catch (error) {
            toast.error('Failed to save credential');
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await accessService.delete(id);
            toast.success('Credential deleted');
            setDeleteConfirmId(null);
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied!`, {
            duration: 2000,
            icon: <Icons.Check className="w-4 h-4 text-green-500" />
        });
    };

    const togglePassword = (id: string) => {
        setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                        <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-200 dark:shadow-none">
                            <Icons.Key className="w-6 h-6 text-white" />
                        </div>
                        ACCESS MANAGER
                    </h1>
                    <p className="mt-2 text-slate-500 dark:text-slate-400 font-medium uppercase tracking-widest text-[10px]">
                        Internal operation credentials & shared logins
                    </p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold shadow-lg shadow-indigo-100 dark:shadow-none transition-all active:scale-95"
                >
                    <Icons.Plus className="w-5 h-5" />
                    ADD CREDENTIAL
                </button>
            </div>

            {/* Search & Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3 relative">
                    <Icons.Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search by website, username, or notes..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-14 pr-6 py-4 bg-white dark:bg-slate-900 border-none rounded-2xl shadow-sm focus:ring-2 focus:ring-indigo-500 transition-all outline-none text-sm font-medium"
                    />
                </div>
                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-sm flex items-center justify-between px-6 border border-slate-100 dark:border-slate-800">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Records</span>
                    <span className="text-2xl font-black text-indigo-600 px-3 py-1 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl">{filteredRecords.length}</span>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Website</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Username</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Password</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-4">
                                            <Icons.Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                                            <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">Loading records...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredRecords.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full">
                                                <Icons.Search className="w-8 h-8 text-slate-400" />
                                            </div>
                                            <span className="text-sm font-bold text-slate-400 uppercase tracking-widest">No credentials found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredRecords.map((record) => (
                                    <tr key={record.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group">
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col gap-1">
                                                <span className="font-bold text-slate-900 dark:text-white">{record.websiteName}</span>
                                                {record.url && (
                                                    <a
                                                        href={record.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs text-indigo-600 hover:underline flex items-center gap-1 font-medium"
                                                    >
                                                        {record.url}
                                                        <Icons.ExternalLink className="w-3 h-3" />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-2">
                                                <code className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs font-bold text-slate-700 dark:text-slate-300">
                                                    {record.username}
                                                </code>
                                                <button
                                                    onClick={() => copyToClipboard(record.username, 'Username')}
                                                    className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                    title="Copy Username"
                                                >
                                                    <Icons.Copy className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-2">
                                                    <code className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs font-bold text-slate-700 dark:text-slate-300">
                                                        {showPasswords[record.id] ? record.password : '••••••••'}
                                                    </code>
                                                    <button
                                                        onClick={() => togglePassword(record.id)}
                                                        className="p-1.5 text-slate-400 hover:text-indigo-600 rounded-lg transition-all"
                                                        title={showPasswords[record.id] ? "Hide" : "Show"}
                                                    >
                                                        {showPasswords[record.id] ? <Icons.EyeOff className="w-4 h-4" /> : <Icons.Eye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => copyToClipboard(record.password, 'Password')}
                                                    className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                    title="Copy Password"
                                                >
                                                    <Icons.Copy className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenModal(record)}
                                                    className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/50 rounded-xl transition-all"
                                                    title="Edit"
                                                >
                                                    <Icons.Edit className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirmId(record.id)}
                                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-xl transition-all"
                                                    title="Delete"
                                                >
                                                    <Icons.Trash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-300 border border-slate-100 dark:border-slate-800">
                        <div className="px-8 py-6 border-b border-slate-50 dark:border-slate-800 flex items-center justify-between bg-indigo-600">
                            <h2 className="text-xl font-black text-white uppercase tracking-tight">
                                {editingRecord ? 'Edit Credential' : 'Add New Credential'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
                                <Icons.X className="w-6 h-6" />
                            </button>
                        </div>
                        <form onSubmit={handleSave} className="p-8 space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Website Name</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.websiteName}
                                        onChange={(e) => setFormData({ ...formData, websiteName: e.target.value })}
                                        placeholder="e.g. Booking.com Extranet"
                                        className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none transition-all font-bold text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">URL (Optional)</label>
                                    <input
                                        type="url"
                                        value={formData.url}
                                        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                        placeholder="https://..."
                                        className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none transition-all font-bold text-sm"
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Username</label>
                                        <input
                                            required
                                            type="text"
                                            value={formData.username}
                                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                            placeholder="User ID or Email"
                                            className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none transition-all font-bold text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Password</label>
                                        <input
                                            required
                                            type="text"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            placeholder="Password"
                                            className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none transition-all font-bold text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Internal Notes</label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        placeholder="Add any specific instructions or recovery info..."
                                        rows={3}
                                        className="w-full px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-indigo-500 rounded-2xl outline-none transition-all font-bold text-sm resize-none"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-6 py-4 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-2 px-12 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-indigo-100 dark:shadow-none transition-all"
                                >
                                    {editingRecord ? 'Update Record' : 'Save Record'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {deleteConfirmId && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center animate-in zoom-in-95 duration-300 border border-slate-100 dark:border-slate-800">
                        <div className="w-20 h-20 bg-red-50 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Icons.Trash2 className="w-10 h-10 text-red-500" />
                        </div>
                        <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-2">Delete Credential?</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mb-8 uppercase tracking-widest leading-relaxed">
                            This action cannot be undone. Are you sure?
                        </p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="flex-1 px-6 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all"
                            >
                                No, Keep it
                            </button>
                            <button
                                onClick={() => handleDelete(deleteConfirmId)}
                                className="flex-1 px-6 py-4 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-red-100 dark:shadow-none transition-all"
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccessManagerView;
